import React, { memo, useMemo } from 'react';
import DefaultMaterialTable from 'common/util/materialTable';
import { optionsBuilder } from 'common/util/materialTable/options';
import { customTheme } from 'common/util/materialTable/theme';
import { dataSourceActions, dataSourceStatus } from '../utils/constants';
import TextAction from './TextAction';
import { colorBlue1, colorBlue3, white } from 'styles/_colors.scss';

const listItemActive = {
    backgroundColor: colorBlue3,
    color: colorBlue1,
};

const inactiveStyle = {
    color: '#28282E',
    opacity: '0.5',
};

const tableTheme = customTheme({
    MuiTableCell: {
        head: {
            '&:last-child': {
                paddingLeft: '26px',
            },
        },
    },

    MuiInputBase: {
        root: {
            '&:nth-child(2)': {
                backgroundColor: `${white}`,
                border: '1px solid #B1B1B1',
                borderRadius: '3px',
                width: '200px',

                '&:has(> .Mui-focused)': {
                    borderColor: colorBlue1,
                },
            },
        },
    },

    MuiSelect: {
        selectMenu: {
            backgroundColor: `${white} !important`,
            color: `${colorBlue1} !important`,
            fontWeight: '600',
            paddingLeft: '10px',
        },
    },

    MuiPopover: {
        paper: {
            backgroundColor: `${white}`,
            border: '1px solid #E4E4E4',
            maxWidth: '180px',
        },
    },

    MuiListItem: {
        root: {
            '&.Mui-selected, &.Mui-selected:hover': listItemActive,
        },

        button: {
            transition: 'none',

            '&:hover': listItemActive,
        },
    },
});

export const dataSourceColumns = {
    text: { key: 'text', label: 'Name', filter: 'Filter by name' },
    status: { key: 'status', label: 'Status', filter: '' },
    actions: { key: 'actions', label: 'Actions', filter: '' },
};

const options = optionsBuilder({
    actionsColumnIndex: -1,
    draggable: false,
    search: false,
    toolbar: false,
    filtering: true,
});

const TextByStatus = ({ status, text }) => (
    <span style={status === dataSourceStatus.inactive.value ? inactiveStyle : {}}>{text}</span>
);

const DataSourceGrid = ({ data, onClickChangeStatus }) => {
    const columns = useMemo(() => {
        return [
            {
                title: dataSourceColumns.text.label,
                field: dataSourceColumns.text.key,
                filterPlaceholder: dataSourceColumns.text.filter,
                defaultSort: 'asc',
                headerStyle: {
                    width: '40%',
                },
                cellStyle: {
                    width: '40%',
                },
                render: rowData => <TextByStatus status={rowData.status} text={rowData[dataSourceColumns.text.key]} data-cy="DataSourceGrid-TextByStatus"/>,
            },
            {
                title: dataSourceColumns.status.label,
                field: dataSourceColumns.status.key,
                filterPlaceholder: dataSourceColumns.status.filter,
                lookup: {
                    [dataSourceStatus.active.value]: dataSourceStatus.active.label,
                    [dataSourceStatus.inactive.value]: dataSourceStatus.inactive.label,
                },
                defaultFilter: [dataSourceStatus.active.value],
                sorting: false,
                headerStyle: {
                    width: '30%',
                },
                cellStyle: {
                    width: '30%',
                },
                render: rowData => (
                    <TextByStatus
                        status={rowData.status}
                        text={
                            rowData.status === dataSourceStatus.inactive.value
                                ? dataSourceStatus.inactive.label
                                : dataSourceStatus.active.label
                        }
                        data-cy="DataSourceGrid-TextByStatuses"
                    />
                ),
            },
            {
                title: dataSourceColumns.actions.label,
                field: dataSourceColumns.actions.key,
                filterPlaceholder: dataSourceColumns.actions.filter,
                sorting: false,
                filtering: false,
                headerStyle: {
                    width: '30%',
                },
                cellStyle: {
                    width: '30%',
                },
                render: rowData => {
                    const text =
                        rowData.status === dataSourceStatus.inactive.value
                            ? dataSourceActions.restore
                            : dataSourceActions.deactivate;
                    return <TextAction text={text} onClick={() => onClickChangeStatus(rowData)} data-cy="DataSourceGrid-onClickChangeStatus"/>;
                },
            },
        ];
    }, []);

    return <DefaultMaterialTable theme={tableTheme} options={options} columns={columns} data={data} data-cy="DataSourceGrid-table"/>;
};

export default memo(DataSourceGrid);
